import React from 'react';
import { Formik } from 'formik';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import { isRecordOnlyEnabledForOrg } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/utils';
import type { PaymentsConfiguration } from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/types';
import type { OrganizationModel } from '~/components/types/organization-types';

// `satisfies keyof PaymentsConfiguration` validates the key against the real response contract at
// compile time — if the field is ever renamed, tsc fails here instead of silently lying (which a
// hand-written `as 'enable_trusted_payee_payments'` cast would not). The literal type still flows
// through, so the Formik id, initialValues key and RequireApprovalValues can never desync.
const ENABLE_TRUSTED_PAYEE_PAYMENTS_FIELD_ID = 'enable_trusted_payee_payments' satisfies keyof PaymentsConfiguration;
const SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS_FIELD_ID =
  'skip_approval_for_record_only_payments' satisfies keyof PaymentsConfiguration;

// Partial, not a plain Record: the record-only switch is available only on its own terms, and the
// save must carry only the fields whose switch was actually rendered. The sysconfig write path
// persists every non-null field it receives, so submitting a hidden switch's `false` would clear a
// stored value the user was never shown — omitting the key is what leaves it untouched.
type RequireApprovalValues = Partial<
  Record<
    typeof ENABLE_TRUSTED_PAYEE_PAYMENTS_FIELD_ID | typeof SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS_FIELD_ID,
    boolean
  >
>;

interface RequireApprovalConfigurationDialogProps {
  paymentsConfiguration: PaymentsConfiguration;
  disabled?: boolean;
  onCancel: () => void;
  onSubmit: (values: RequireApprovalValues) => Promise<void>;
}

const RequireApprovalConfigurationDialog: React.FC<RequireApprovalConfigurationDialogProps> = ({
  paymentsConfiguration,
  disabled = false,
  onCancel,
  onSubmit,
}) => {
  const { classes } = useStyles();
  // ConfigSwitch renders this dialog with only the configuration and the callbacks, so availability
  // is derived here from the sysconfig context exactly as the parent tab derives it.
  // `SysconfigContext` is still JS (`createContext({})`), so the hook infers `{}` and reading a
  // member off it needs an assertion. Asserting the member's real type gives `organization` a
  // type here, which suppressing the line would not.
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const isRecordOnlyEnabled = isRecordOnlyEnabledForOrg(paymentsConfiguration, organization);

  return (
    <Formik<RequireApprovalValues>
      initialValues={{
        [ENABLE_TRUSTED_PAYEE_PAYMENTS_FIELD_ID]: !!paymentsConfiguration?.[ENABLE_TRUSTED_PAYEE_PAYMENTS_FIELD_ID],
        ...(isRecordOnlyEnabled
          ? {
              [SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS_FIELD_ID]:
                !!paymentsConfiguration?.[SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS_FIELD_ID],
            }
          : {}),
      }}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
        } catch (error) {
          formikProps.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          title="Require approval for every payment"
          isDialog
          onClose={onCancel}
          preventClose={disabled || isSubmitting}
          maxWidth="xs"
        >
          <div className="flex flex-col gap-8">
            <div>
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                When enabled, payments to trusted payees are exempt from the per-payment approval requirement.
              </Text>
              <SwitchFormik id={ENABLE_TRUSTED_PAYEE_PAYMENTS_FIELD_ID} label="Enable trusted payee payments" />
            </div>
            {isRecordOnlyEnabled ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  When enabled, record-only payments are approved automatically and raise no transaction request, since
                  no funds are disbursed.
                </Text>
                <SwitchFormik
                  id={SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS_FIELD_ID}
                  label="Skip approval for record-only payments"
                />
              </div>
            ) : null}
          </div>
          <div className={classes.buttonsContainer}>
            <CancelButton disabled={disabled || isSubmitting} onClick={onCancel} />
            <Button
              variant="contained"
              color="primary"
              disabled={disabled || isSubmitting}
              onClick={() => handleSubmit()}
            >
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default RequireApprovalConfigurationDialog;
